import React, { useState } from 'react';
import './AutoRunBadge.css';

// HS-155: dashboard status pill for a project's autonomous (server-driven) run.
// Reads the `autoRun` summary attached to each project by the backend
// (GET /api/projects). Renders nothing when the project has no autonomous run.
// Template-agnostic — lights up unchanged for fem/spt runs once those are wired.

const TERMINAL = { done: true, failed: true, cancelled: true };

// Keyed on runId (not project id alone) so a NEW run's terminal state always
// reappears even if a prior run on the same project was dismissed.
function seenKey(projectId, runId) {
  return `hidenn.autoRunSeen.${projectId}.${runId || ''}`;
}
function isSeen(projectId, runId) {
  try {
    return localStorage.getItem(seenKey(projectId, runId)) === '1';
  } catch {
    return false;
  }
}

export default function AutoRunBadge({ project }) {
  // Bump on dismiss to re-render; dismissed state itself is read from
  // localStorage every render, so it always reflects the current runId.
  const [, forceTick] = useState(0);

  const run = project?.autoRun;
  if (!run || !run.status) return null;

  const isTerminal = !!TERMINAL[run.status];
  if (isTerminal && isSeen(project.projectId, run.runId)) return null;

  let label;
  let cls;
  switch (run.status) {
    case 'running': {
      const n = run.stepCount || 0;
      const i = (run.stepIndex ?? 0) + 1;
      label = n ? `🎓 Autonomous · Step ${Math.min(i, n)}/${n}` : '🎓 Autonomous · running';
      cls = 'running';
      break;
    }
    case 'done':
      label = '✓ Complete';
      cls = 'done';
      break;
    case 'failed':
      label = '⚠ Failed';
      cls = 'failed';
      break;
    case 'cancelled':
      label = 'Cancelled';
      cls = 'cancelled';
      break;
    default:
      return null;
  }

  function dismiss(e) {
    e.stopPropagation();
    try {
      localStorage.setItem(seenKey(project.projectId, run.runId), '1');
    } catch {
      /* ignore quota/availability errors */
    }
    forceTick((t) => t + 1);
  }

  return (
    <span className={`autorun-badge autorun-${cls}`} title={run.error || undefined}>
      <span className="autorun-label">{label}</span>
      {isTerminal && (
        <button
          type="button"
          className="autorun-dismiss"
          onClick={dismiss}
          title="Dismiss"
          aria-label="Dismiss autonomous-run status"
        >
          ×
        </button>
      )}
    </span>
  );
}
